/**
 * Web app root — manages auth bootstrap on mount.
 *
 * On first render we try to restore a session from a saved refresh token.
 * - Success → user is authenticated, child routes render normally.
 * - No token / refresh failed → user must log in via OTP (handled by
 *   the auth-layout child routes).
 *
 * No Telegram SDK here — this is the browser/PWA entry point.
 */

import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import { Outlet } from 'react-router-dom'
import { useAuthStore } from '@turnifly/shared/stores'
import type { Profile } from '@turnifly/shared/types'
import {
  getSavedTokens,
  refreshToken,
  saveTokens,
  clearTokens,
} from '@turnifly/shared/api'
import { LoadingSpinner, Logo } from '@turnifly/shared/ui'
import { usePendingPayment } from '@turnifly/shared/hooks'
import { PaymentResultModal } from './features/profile/components/payment-result-modal'

function SplashScreen(): ReactNode {
  return (
    <div className="min-h-dvh bg-bg flex flex-col items-center justify-center gap-8">
      <Logo size="lg" />
      <LoadingSpinner size="md" />
    </div>
  )
}

export function App(): ReactNode {
  const isLoading = useAuthStore((s) => s.isLoading)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const userId = useAuthStore((s) => s.user?.id)
  const initDone = useRef(false)
  const pendingPayment = usePendingPayment(isAuthenticated ? userId : undefined)

  useEffect(() => {
    if (initDone.current) return
    initDone.current = true

    const store = useAuthStore.getState()
    const { refreshToken: savedRefresh } = getSavedTokens()

    if (!savedRefresh) {
      // No session — drop to login. Auth gate in MainLayout handles the redirect.
      store.setAuthenticated(false)
      store.setLoading(false)
      return
    }

    refreshToken(savedRefresh)
      .then((res) => {
        saveTokens(res.access_token, res.refresh_token)
        store.setUser(res.user as Profile)
        store.setAuthenticated(true)
        store.setNewUser(!res.user.onboarding_completed)
      })
      .catch(() => {
        // Refresh expired or invalid → force re-login.
        clearTokens()
        store.setAuthenticated(false)
        store.setNewUser(false)
      })
      .finally(() => store.setLoading(false))
  }, [])

  if (isLoading) return <SplashScreen />

  return (
    <div className="min-h-dvh font-body text-text bg-bg overflow-x-hidden w-full relative">
      <Outlet />
      <PaymentResultModal
        payment={pendingPayment.payment}
        isOpen={pendingPayment.isOpen}
        onClose={pendingPayment.close}
        onUpdate={pendingPayment.updatePayment}
      />
    </div>
  )
}
